import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { InterviewResultOffer } from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';

import { IconName } from '../../icons-container/icons.type';
import { ComponentVariant, Variants } from '../variant/variant';
import { IconSizes } from './../icon/icon.module';

enum RatingLevel {
  No = 'no',
  Neutral = 'neutral',
  Yes = 'yes',
  StrongYes = 'strong_yes',
}

@Component({
  selector: 'rt-interview-evaluation-rating',
  templateUrl: 'interview-evaluation-rating.component.html',
  styleUrls: ['./interview-evaluation-rating.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'variant',
  },
})
export class InterviewEvaluationRatingComponent {
  public static readonly VARIANTS: Variants = ['default', ['default', 'hasLabel', 'other']];

  @ComponentVariant(InterviewEvaluationRatingComponent.VARIANTS)
  @Input()
  public variant: string;

  @Input()
  public set rating(value: number | string) {
    if (typeof value === 'number') {
      this.ratingKey = this.getRatingName(value);
    } else {
      this.ratingKey = this.mapStringToRating(<RatingLevel>value);
    }
  }
  @Input() public stage: any;
  @Input() public size: keyof typeof IconSizes = 'medium';
  @Input() public centered: boolean = true;
  @Input() public tooltip: boolean = true;
  @Input() public colorize: boolean = true;
  @Input() public offer?: InterviewResultOffer;

  public ratingKey: RatingLevel;
  public readonly iconMap: { [key: string]: IconName } = {
    [RatingLevel.StrongYes]: 'rating-double-thumb-up',
    [RatingLevel.Yes]: 'rating-thumb-up',
    [RatingLevel.Neutral]: 'rating-thumbs-up-down',
    [RatingLevel.No]: 'rating-thumb-down',
  };

  constructor(private i18n: I18nService) {}

  public isGreen(rating: RatingLevel): boolean | null {
    return this.hasRating([RatingLevel.StrongYes, RatingLevel.Yes], rating);
  }

  public isGrey(rating: RatingLevel): boolean | null {
    return this.hasRating([RatingLevel.No, RatingLevel.Neutral], rating);
  }

  public hasVariant(variant: string): boolean {
    return (this.variant || '').includes(variant);
  }

  public getRatingTranslation(ratingKey: RatingLevel): string {
    switch (ratingKey) {
      case RatingLevel.StrongYes:
        return this.i18n.translate('rtu.interview.summary.strong_yes');
      case RatingLevel.Yes:
        return this.i18n.translate('rtu.interview.summary.yes');
      case RatingLevel.Neutral:
        return this.i18n.translate('rtu.interview.summary.neutral');
      case RatingLevel.No:
        return this.i18n.translate('rtu.interview.summary.no');
      default:
        return '―';
    }
  }

  private hasRating(ratings: RatingLevel[], rating: RatingLevel): true | null {
    return ratings.includes(rating) || null;
  }

  private getRatingName(rating: number): RatingLevel {
    if (rating <= 25) {
      return RatingLevel.No;
    } else if (rating <= 50) {
      return RatingLevel.Neutral;
    } else if (rating <= 75) {
      return RatingLevel.Yes;
    } else {
      return RatingLevel.StrongYes;
    }
  }

  private mapStringToRating(name: RatingLevel): RatingLevel {
    const ratingMap = {
      strong_yes: RatingLevel.StrongYes,
      yes: RatingLevel.Yes,
      neutral: RatingLevel.Neutral,
      no: RatingLevel.No,
    };

    return ratingMap[name];
  }
}
